/**
 *
 * CoursesMobile
 *
 */

import React, { memo, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';
import { useTranslation } from "react-i18next";

import moment from "utils/moment/momentLocale";

// styles
import './style.scss';

import { useInjectSaga } from 'utils/injectSaga';
import { useInjectReducer } from 'utils/injectReducer';
import reducer from './reducer';
import saga from './saga';

import { flushState, loadMobileCourses, updateCourse } from './actions';
import {
  makeSelectLoading,
  makeSelectError,
  makeSelectItemsList,
  makeSelectCourse,
  makeSelectAppointments
} from './selectors';

// antd component
import { Layout, Switch } from 'antd';
const { Content } = Layout;

// components
import TCFCalendar from 'components/TCFCalendar';
// inner components
import { CoursesList } from './components';

// global user
import { useGetUserChildren } from 'engine/Contexts/User.context';

// utils
import { DAY_OF_WEEK_OPTIONS } from 'utils/helpers/helper.constant';
import { getObjId, getArrayIds } from 'utils/helpers/helper.general';
import { useRefState } from 'utils/hooks';
import _local from 'utils/localStorageUtils';

export function CoursesMobile({
  // main data
  courseDetail,
  itemsList,
  appointments,
  firstElem,
  // states
  history,
  error,
  loading,
  // actions
  loadMobileCourses,
  updateCourse,
  flushState,
}) {
  useInjectReducer({ key: 'coursesMobile', reducer });
  useInjectSaga({ key: 'coursesMobile', saga });

  const { t, i18n } = useTranslation();

  const now = moment();
  const week = now.isoWeekday();
  const localUser = _local.getLocalUser();
  const localActiveChild = _local.getLocalChild();
  const children = useGetUserChildren()();

  const getDaysWeek = useCallback(() => DAY_OF_WEEK_OPTIONS(), [i18n?.language]);

  let initOptions = {
    course_week: getDaysWeek()[week - 1]?.value,
    onDate: now
  };

  if (localUser?.role == 'parent') {
    initOptions.students = getArrayIds(children);
  } else if (localUser?.role == 'teacher') {
    initOptions._id = localUser?._id;
  }

  const [child, setChild] = useState(localActiveChild || null);
  const [selectedDate, setSelectedDate] = useState(now);

  const [optionsData, setOptionsData] = useState(initOptions);
  const [showAll, showAllRef, setShowAll] = useRefState(false);

  const [currentCourses, setCurrentCourses] = useState([]);
  const [currentAppointments, setCurrentAppointments] = useState([]);

  // Clearing the state after unmounting a component
  // - work like ComponentDidUpdate
  useEffect(() => {
    (!itemsList || !appointments) && loadMobileCourses(optionsData);
    return () => {
      // Anything in here is fired on component unmount.
      flushState();
    };
  }, []);

  // Loading data when optionsData is changed
  // - work like ComponentDidUpdate
  useEffect(() => {
    loadMobileCourses(optionsData);
  }, [optionsData]);

  useEffect(() => {
    if (!!itemsList && itemsList.length > 0 || !!appointments && appointments?.length > 0) {
      if (localUser?.role == 'parent' && child && !showAll) {
        const listStd = getActiveChildCourses(itemsList);
        const pointStd = getActiveChildAppointments(appointments);
        setCurrentCourses(listStd);
        setCurrentAppointments(pointStd);
      } else {
        setCurrentCourses(itemsList);
        setCurrentAppointments(appointments);
      }
    } else {
      setCurrentCourses([]);
      setCurrentAppointments([]);
    }
  }, [itemsList, child, showAll]);

  useEffect(() => {
    if (getObjId(localActiveChild) != getObjId(child)) {
      setChild(localActiveChild);
    }
  }, [localActiveChild?._id]);

  const getActiveChildCourses = useCallback(list => {
    if (localUser?.role == 'parent' && child && list) {
      return list.filter(course =>
        course?.students.find(el => getObjId(el) == getObjId(child)),
      );
    } else {
      return [];
    }
  }, [child, localUser]);
  
  const getActiveChildAppointments = useCallback(list => {
    if (localUser?.role == 'parent' && child && list) {
      return list.filter(point =>
        getObjId(point?.adminAnswer?.student) == getObjId(child)
      );
    } else {
      return [];
    }
  }, [child, localUser]);

  const onCalendarSelect = useCallback(
    value => {
      const date = moment(value);
      setSelectedDate(date);
      // 1 - Monday, 7 - Sunday
      const week2 = value.isoWeekday();
      const datas = {
        ...optionsData,
        course_week: getDaysWeek()[week2 - 1]?.value,
        onDate: date.toISOString(),
      };
      setOptionsData(datas);
    },
    [optionsData, i18n?.language],
  );

  const showAllHandle = useCallback(() => {
    setShowAll(prev => {
      if (!prev === true) {
        setCurrentCourses(itemsList);
      } else if (!prev === false) {
        const stdC = getActiveChildCourses(itemsList);
        setCurrentCourses(stdC);
      }
      return !prev;
    });
  }, [itemsList]);

  // render function
  return (
    <Layout className="main-full-content">
      <Helmet>
        <title>{t("labels.course")}</title>
        <meta name="description" content="Description of Cours" />
      </Helmet>
      <Content className="main-content-container courses-page">
        <TCFCalendar valueState={selectedDate} onSelect={onCalendarSelect} />

        <CoursesList
          selectedDate={moment(selectedDate).toISOString()}
          timeZone={moment(selectedDate).utcOffset()}
          dataSourse={currentCourses}
          appointments={currentAppointments}
          className="courses-list-wrapper"
          showAll={showAll}
          loading={loading}
        />

        {localUser?.role == "parent" && (
          <div className="courses-list-wrapper__switch">
            <Switch
              className="switch-btn"
              checked={showAll}
              onChange={showAllHandle}
            />
            <p className="switch-title">{t("students.lessonPlan")}</p>
          </div>
        )}
      </Content>
    </Layout>
  );
}

CoursesMobile.propTypes = {
  dispatch: PropTypes.func.isRequired,
  flushState: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

const mapStateToProps = createStructuredSelector({
  loading: makeSelectLoading(),
  error: makeSelectError(),
  itemsList: makeSelectItemsList(),
  appointments: makeSelectAppointments(),
  courseDetail: makeSelectCourse(),
});

function mapDispatchToProps(dispatch) {
  return {
    dispatch,
    flushState: () => dispatch(flushState()),
    loadMobileCourses: options => dispatch(loadMobileCourses(options)),
    updateCourse: itemData => dispatch(updateCourse(itemData)),
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  memo
)(CoursesMobile);
